import React, { useEffect, useState, useRef } from 'react'
import { Col, Row, Spin, Button, Tag, Icon } from 'antd'
import Auxiliary from '../../util/Auxiliary'
import DeviceMap from '../DeviceMap/Map'
import axios from '../../util/Api'
import moment from 'moment'
import './style.css'

const logoPath = '/icons/Logo-Assetpro.png'

function PublicTracking (props) {
  const [assetId, setAssetId] = useState(null)
  const [device, setDevice] = useState(null)
  const [token, setToken] = useState(null)
  const [assetData, setAssetData] = useState(null)
  const [deviceData, setDeviceData] = useState(null)
  const [deviceAssetData, setDeviceAssetData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isLiveTracking, setIsLiveTracking] = useState(false)
  const [isTokenValid, setIsTokenValid] = useState(false)
  const [expiryTimestamp, setExpiryTimestamp] = useState(null)
  const pollingIntervalRef = useRef(null)
  const autoStopTimeoutRef = useRef(null)

  // Get assetId, device (imei), and token from URL params
  useEffect(() => {
    if (!props.match || !props.match.params) return
    const aid = props.match.params.asset
    const id = props.match.params.id
    const tok = props.match.params.token
    if (aid) {
      setAssetId(aid)
    }
    if (id) {
      setDevice(id)
    }
    if (tok) {
      setToken(tok)
    }
  }, [props.match && props.match.params])

  // Validate token on mount
  useEffect(() => {
    if (!token || !assetId || !device) {
      setError('Invalid URL')
      setLoading(false)
      return
    }

    const validateToken = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await axios.get(`/live-track/validate-token/${token}/${assetId}/${device}`)
        if (response && response.data && response.data.isValid === true) {
          setIsTokenValid(true)
          setExpiryTimestamp(response.data.expiryTimestamp || null)
          setError(null)
        } else {
          setIsTokenValid(false)
          setExpiryTimestamp(null)
          setError('Invalid URL')
        }
        setLoading(false)
      } catch (err) {
        console.error('Error validating token:', err)
        setIsTokenValid(false)
        setExpiryTimestamp(null)
        setError('Invalid URL')
        setLoading(false)
      }
    }

    validateToken()
  }, [token, assetId, device])

  // Fetch asset details (only if token is valid)
  useEffect(() => {
    if (!assetId || !isTokenValid) return

    const fetchAssetDetails = async () => {
      try {
        const response = await axios.get(`/asset/public/${assetId}`)
        if (response && response.data) {
          setAssetData(response.data)
          // Find the device asset from dependents
          if (response.data.dependents && response.data.dependents.length && device) {
            const deviceAsset = response.data.dependents.find(
              dp => dp && dp.tagDeviceId && dp.tagDeviceId === device
            )
            if (deviceAsset) {
              setDeviceAssetData(deviceAsset)
            }
          }
        }
      } catch (err) {
        console.error('Error fetching asset details:', err)
        setError('Failed to load asset details')
      }
    }

    fetchAssetDetails()
  }, [assetId, device, isTokenValid])

  // Initial fetch on mount (only if token is valid)
  useEffect(() => {
    if (!assetId || !device || !isTokenValid) return

    const fetchDeviceData = async () => {
      try {
        const response = await axios.get(`/iot-device-data/public/${device}/${assetId}`)
        if (response && response.data) {
          setDeviceData(response.data)
        }
      } catch (err) {
        console.error('Error fetching device data:', err)
      }
    }

    fetchDeviceData()
  }, [assetId, device, isTokenValid])

  // Handle start live tracking - validate token first
  const handleStartLiveTracking = async () => {
    if (!token || !assetId || !device) {
      setError('Invalid URL')
      setIsTokenValid(false)
      setExpiryTimestamp(null)
      return
    }

    try {
      const response = await axios.get(`/live-track/validate-token/${token}/${assetId}/${device}`)
      if (response && response.data && response.data.isValid === true) {
        setIsTokenValid(true)
        setExpiryTimestamp(response.data.expiryTimestamp || null)
        setIsLiveTracking(true)
        setError(null)
      } else {
        setIsTokenValid(false)
        if (response && response.data && response.data.expiryTimestamp) {
          setExpiryTimestamp(response.data.expiryTimestamp)
          setError('Tracking url is expired')
        } else {
          setExpiryTimestamp(null)
          setError('Invalid URL')
        }
        setIsLiveTracking(false)
      }
    } catch (err) {
      console.error('Error validating token:', err)
      setIsTokenValid(false)
      setExpiryTimestamp(null)
      setIsLiveTracking(false)
      setError('Invalid URL')
    }
  }

  // Set up polling when live tracking is enabled (only if token is valid)
  useEffect(() => {
    if (!assetId || !device || !isLiveTracking || !isTokenValid) {
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current)
        autoStopTimeoutRef.current = null
      }
      return
    }

    const fetchDeviceData = async () => {
      try {
        const response = await axios.get(`/iot-device-data/public/${device}/${assetId}`)
        if (response && response.data) {
          setDeviceData(response.data)
        }
      } catch (err) {
        console.error('Error fetching device data:', err)
      }
    }

    fetchDeviceData()

    pollingIntervalRef.current = setInterval(() => {
      fetchDeviceData()
    }, 10000)

    autoStopTimeoutRef.current = setTimeout(() => {
      setIsLiveTracking(false)
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }, 300000)

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current)
        autoStopTimeoutRef.current = null
      }
    }
  }, [assetId, device, isLiveTracking, isTokenValid])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current)
      }
    }
  }, [])

  const getStatusColor = (status) => {
    switch (status && status.toLowerCase()) {
      case 'moving': return '#52c41a'
      case 'idle': return '#faad14'
      case 'stopped': return '#f5222d'
      case 'offline': return '#8c8c8c'
      default: return '#1890ff'
    }
  }

  const getExpiryInfo = () => {
    if (!expiryTimestamp) return null
    const expiryTime = moment(expiryTimestamp)
    const now = moment()
    const duration = moment.duration(expiryTime.diff(now))

    if (duration.asMinutes() <= 0) {
      return { text: 'Link Expired', color: '#f5222d' }
    } else if (duration.asHours() < 24) {
      return { text: expiryTime.format('DD MMM, hh:mm A'), color: '#faad14' }
    } else {
      return { text: expiryTime.format('DD MMM, hh:mm A'), color: '#52c41a' }
    }
  }

  const organisationName = assetData && assetData.organisation && assetData.organisation.name || 'AssetPro'
  const expiryInfo = getExpiryInfo()

  return (
    <Auxiliary>
      <div className='public-tracking-page' style={styles.pageContainer}>
        {/* Header */}
        <header className='public-tracking-header' style={styles.header}>
          <div style={styles.headerContent}>
            <img
              src={logoPath}
              alt='AssetPro'
              style={styles.logo}
            />
          </div>
          {isLiveTracking && (
            <div className='live-indicator live-indicator-badge' style={styles.liveIndicator}>
              <span className='live-dot' style={styles.liveDot} />
              <span style={styles.liveText}>LIVE</span>
            </div>
          )}
        </header>

        {/* Main Content */}
        <main style={styles.mainContent}>
          <Spin spinning={loading}>
            {(!loading && !isTokenValid) || error ? (
              <div style={styles.errorContainer}>
                <Icon type='warning' style={styles.errorIcon} />
                <h2 style={styles.errorTitle}>{error || 'Invalid URL'}</h2>
                <p style={styles.errorText}>
                  Please check the link and try again or request a new tracking link.
                </p>
              </div>
            ) : isTokenValid ? (
              <>
                {/* Asset Info Widget - Full Width */}
                <div className='asset-info-card glass-card' style={styles.assetInfoCard}>
                  <Row gutter={[12, 0]} type='flex' align='middle'>
                    <Col xs={5} sm={4} md={4} lg={3}>
                      <div className='asset-icon-wrapper' style={styles.assetIconWrapper}>
                        <img
                          alt='Vehicle'
                          src={require('assets/images/widget/fixed-asset.png')}
                          className='asset-icon'
                          style={styles.assetIcon}
                        />
                      </div>
                    </Col>
                    <Col xs={19} sm={14} md={12} lg={13}>
                      <div className='asset-details' style={styles.assetDetails}>
                        <h1 className='asset-title' style={styles.assetTitle}>
                          {assetData && assetData.assetCode || '-'}
                          {assetData && assetData.altName && (
                            <span className='asset-alt-name' style={styles.assetAltName}>({assetData.altName})</span>
                          )}
                          {assetData && assetData.ownerCode && (
                            <span className='owner-code' style={styles.ownerCode}>{assetData.ownerCode}</span>
                          )}
                        </h1>
                        <div className='meta-items' style={styles.assetMeta}>
                          <span style={styles.metaItem}>
                            <Icon type='car' style={styles.metaIcon} />
                            {assetData && assetData.assetModel && assetData.assetModel.name || 'Vehicle'}
                          </span>
                          <span style={styles.metaItem}>
                            <Icon type='environment' style={styles.metaIcon} />
                            {assetData && assetData.location && assetData.location.name || '-'}
                          </span>
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} sm={6} md={8} lg={8}>
                      <div className='status-section' style={styles.statusSection}>
                        {deviceData && deviceData.timestamp && (
                          <div className='lastUpdatedBadge' style={styles.lastUpdatedBadge}>
                            <Icon type='sync' style={{ marginRight: 4 }} />
                            <span style={styles.badgeLabel}>Updated:</span> {moment(deviceData.timestamp).format('DD MMM, hh:mm A')}
                          </div>
                        )}
                        {expiryInfo && (
                          <div className='expiryBadge' style={{ ...styles.expiryBadge, backgroundColor: expiryInfo.color + '20', color: expiryInfo.color }}>
                            <Icon type='clock-circle' style={{ marginRight: 4 }} />
                            <span style={styles.badgeLabel}>Expires:</span> {expiryInfo.text}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Map Container */}
                <div className='map-container' style={styles.mapContainer}>
                  {/* Live Tracking Button */}
                  <div style={styles.trackingButtonContainer}>
                    <Button
                      type={isLiveTracking ? 'danger' : 'primary'}
                      icon={isLiveTracking ? 'pause-circle' : 'play-circle'}
                      size='large'
                      onClick={() => {
                        if (isLiveTracking) {
                          setIsLiveTracking(false)
                        } else {
                          handleStartLiveTracking()
                        }
                      }}
                      style={styles.trackingButton}
                    >
                      {isLiveTracking ? 'Stop Live Tracking' : 'Start Live Tracking'}
                    </Button>
                  </div>

                  <DeviceMap
                    deviceData={deviceData}
                    asset={assetData}
                    rangeData={[]}
                    showRangeData={false}
                    mapSpeed={1}
                    source='public'
                  />

                  {/* Map Overlay Info */}
                  {/* {deviceData && (
                    <div className='map-overlay glass-card' style={styles.mapOverlay}>
                      <div style={styles.overlayItem}>
                        <Icon type='clock-circle' style={styles.overlayIcon} />
                        Last Update: {deviceData.timestamp ? moment(deviceData.timestamp).format('DD MMM, hh:mm:ss A') : '-'}
                      </div>
                      {deviceData.address && (
                        <div style={styles.overlayItem}>
                          <Icon type='pushpin' style={styles.overlayIcon} />
                          {deviceData.address}
                        </div>
                      )}
                    </div>
                  )} */}
                </div>
              </>
            ) : null}
          </Spin>
        </main>

        {/* Footer */}
        <footer style={styles.footer}>
          <div className='footer-content' style={styles.footerContent}>
            <div style={styles.orgInfo}>
              <Icon type='bank' style={styles.footerIcon} />
              <span>Subscriber license : <strong>{organisationName}</strong></span>
            </div>
            <div style={styles.copyright}>
              © {new Date().getFullYear()} AssetPro. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </Auxiliary>
  )
}

const styles = {
  pageContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 50%, #d9e2ec 100%)'
  },
  header: {
    background: 'rgba(255, 255, 255, 0.98)',
    padding: '12px 24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  logo: {
    height: 42,
    objectFit: 'contain'
  },
  liveIndicator: {
    position: 'absolute',
    right: 24,
    display: 'flex',
    alignItems: 'center',
    padding: '6px 14px',
    background: 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)',
    borderRadius: 20,
    boxShadow: '0 2px 8px rgba(255, 77, 79, 0.4)'
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#fff',
    marginRight: 8
  },
  liveText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.2
  },
  mainContent: {
    flex: 1,
    padding: '20px',
    overflow: 'auto'
  },
  assetInfoCard: {
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 16,
    padding: '24px',
    marginBottom: 20,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    backdropFilter: 'blur(10px)'
  },
  assetIconWrapper: {
    width: 80,
    height: 80,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)'
  },
  assetIcon: {
    width: 48,
    height: 48,
    filter: 'brightness(0) invert(1)'
  },
  assetDetails: {
    padding: '0 16px'
  },
  assetTitle: {
    fontSize: 24,
    fontWeight: 700,
    margin: 0,
    color: '#1a1a2e',
    lineHeight: 1.3
  },
  assetAltName: {
    fontSize: 16,
    fontWeight: 400,
    color: '#8c8c8c',
    marginLeft: 8
  },
  ownerCode: {
    fontSize: 14,
    fontWeight: 500,
    color: '#667eea',
    marginLeft: 12,
    padding: '2px 8px',
    background: 'rgba(102, 126, 234, 0.1)',
    borderRadius: 4
  },
  assetMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 12
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 14,
    color: '#595959'
  },
  metaIcon: {
    marginRight: 6,
    color: '#667eea'
  },
  deviceInfo: {
    marginTop: 12,
    display: 'flex',
    alignItems: 'center'
  },
  deviceText: {
    fontSize: 13,
    color: '#8c8c8c',
    fontFamily: 'monospace'
  },
  statusSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 12
  },
  statusTag: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    border: 'none'
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
    marginRight: 8
  },
  speedDisplay: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 4
  },
  speedValue: {
    fontSize: 36,
    fontWeight: 700,
    color: '#1a1a2e',
    lineHeight: 1
  },
  speedUnit: {
    fontSize: 14,
    color: '#8c8c8c',
    fontWeight: 500
  },
  lastUpdatedBadge: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: 'rgba(24, 144, 255, 0.1)',
    color: '#1890ff'
  },
  expiryBadge: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600
  },
  badgeLabel: {
    fontWeight: 400,
    marginRight: 4,
    opacity: 0.8
  },
  mapContainer: {
    borderRadius: 16,
    overflow: 'hidden',
    height: 'calc(100vh - 300px)',
    minHeight: 300,
    maxHeight: 'calc(100vh - 300px)',
    position: 'relative',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    background: '#fff'
  },
  trackingButtonContainer: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1000
  },
  trackingButton: {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    borderRadius: 8,
    fontWeight: 600
  },
  mapOverlay: {
    position: 'absolute',
    bottom: 16,
    left: 16,
    background: 'rgba(255, 255, 255, 0.95)',
    padding: '12px 16px',
    borderRadius: 10,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    maxWidth: 'calc(100% - 32px)',
    backdropFilter: 'blur(10px)'
  },
  overlayItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    color: '#1a1a2e',
    marginBottom: 6
  },
  overlayIcon: {
    marginRight: 8,
    color: '#667eea'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    padding: 40,
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 16,
    textAlign: 'center'
  },
  errorIcon: {
    fontSize: 64,
    color: '#faad14',
    marginBottom: 24
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: 600,
    color: '#1a1a2e',
    margin: 0
  },
  errorText: {
    fontSize: 16,
    color: '#8c8c8c',
    marginTop: 12
  },
  footer: {
    background: 'rgba(255, 255, 255, 0.8)',
    padding: '16px 24px',
    backdropFilter: 'blur(10px)',
    borderTop: '1px solid rgba(0, 0, 0, 0.06)'
  },
  footerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12
  },
  orgInfo: {
    display: 'flex',
    alignItems: 'center',
    color: '#1a1a2e',
    fontSize: 14
  },
  footerIcon: {
    marginRight: 8,
    color: '#667eea'
  },
  copyright: {
    color: '#8c8c8c',
    fontSize: 12
  }
}

export default PublicTracking
